import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import apiService from './utils/api';
import { ToastProvider } from './components/Toast';
import Login from './pages/Login';
import Home from './pages/Home';
import TabelaPage from './pages/portfolio/tabela';
import KhataPage from './pages/portfolio/khata';
import EmployeePage from './pages/portfolio/Employee';
import CustomersPage from './pages/portfolio/customers';
import AnimalsPage from './pages/portfolio/animals';
import DailyMilkPage from './pages/produce/dailyKhataMilk';
import WeeklyAnimalPage from './pages/produce/animalRecord';
import PurchaseHistoryPage from './pages/accounting/sellingHistory'; 
import PaymentHistoryPage from './pages/accounting/paymentHistory';    
import BillGeneratorPage from './pages/billing/billGeneratorPage';
import MilkPurchase from './pages/accounting/milkPurchase';
import GoodsPurchase from './pages/accounting/goodsPurchase';
import SupplierPayment from './pages/accounting/supplierPayment';
import AnimalPurchasePage from './pages/accounting/animalPurchase';
import AdminDashboard from './pages/admin/AdminDashboard';
import SettingsPage from './pages/settings/Settings';
import VendorsPage from './pages/portfolio/vendors';
import DairyFarmPage from './pages/portfolio/DairyFarm';
import PartyLedgerPage from './pages/billing/partyLedger';
import CashbookPage from './pages/accounting/cashbook';
import BankStatementPage from './pages/accounting/bankStatement';
import OwnerDashboardPage from './pages/admin/OwnerDashboard';
import useTableLabels from './hooks/useTableLabels';

// Authentication Context
export const AuthContext = React.createContext();

// Protected Route Component
function ProtectedRoute({ children, requiredPermission = null, requiredSection = null }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [hasPermission, setHasPermission] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!apiService.isAuthenticated()) {
          setIsAuthenticated(false);
          return;
        }

        // Verify token with backend
        await apiService.verifyToken();
        setIsAuthenticated(true);

        // Check permissions if required
        if (requiredSection && requiredPermission) {
          const permitted = apiService.hasPermission(requiredSection, requiredPermission);
          setHasPermission(permitted);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        apiService.logout();
      }
    };

    checkAuth();
  }, [requiredPermission, requiredSection]);

  if (isAuthenticated === null) {
    return (
      <div className="loading-container">
        <div className="loading">Verifying authentication...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!hasPermission) {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
        <button onClick={() => navigate('/home')}>Go to Home</button>
      </div>
    );
  }

  return children;
}

// Auth Provider Component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (apiService.isAuthenticated()) {
          const response = await apiService.verifyToken();
          setUser(response.user);
          setPermissions(response.permissions);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        apiService.logout();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    const response = await apiService.login(credentials);
    setUser(response.user);
    setPermissions(response.permissions);
    return response;
  };

  const logout = async () => {
    await apiService.logout();
    setUser(null);
    setPermissions({});
  };

  const value = {
    user,
    permissions,
    login,
    logout,
    isAuthenticated: !!user,
    hasPermission: (section, item) => apiService.hasPermission(section, item),
    hasRole: (role) => user?.role === role
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

function App() {
  useTableLabels(); // Automatically apply data labels for responsive tables
  
  return (
    <ToastProvider>
    <AuthProvider>
      <Routes>
        <Route path="/" element={<Login />} />
        
        <Route path="/home" element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        } />
        
        {/* Admin God Mode Route */}
        <Route path="/admin" element={
          <ProtectedRoute requiredSection={null} requiredPermission={null}>
            {/* The component itself checks if user.role === 'admin' */}
            <AdminDashboard />
          </ProtectedRoute>
        } />
        
        {/* Portfolio section routes - Owner only */}
        <Route path="/tabela" element={
          <ProtectedRoute requiredSection="portfolio" requiredPermission="tabela">
            <TabelaPage />
          </ProtectedRoute>
        } />
        
        <Route path="/khata" element={
          <ProtectedRoute requiredSection="portfolio" requiredPermission="khata">
            <KhataPage />
          </ProtectedRoute>
        } />
        
        <Route path="/employees" element={
          <ProtectedRoute requiredSection="portfolio" requiredPermission="employees">
            <EmployeePage />
          </ProtectedRoute>
        } />
        
        <Route path="/animals" element={
          <ProtectedRoute requiredSection="portfolio" requiredPermission="animals">
            <AnimalsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/customers" element={
          <ProtectedRoute requiredSection="portfolio" requiredPermission="customers">
            <CustomersPage />
          </ProtectedRoute>
        } />

        <Route path="/vendors" element={
          <ProtectedRoute requiredSection="portfolio" requiredPermission="vendors">
            <VendorsPage />
          </ProtectedRoute>
        } />

        <Route path="/dairy-farm" element={
          <ProtectedRoute requiredSection="portfolio" requiredPermission="dairy-farm">
            <DairyFarmPage />
          </ProtectedRoute>
        } />
        
        {/* Produce section routes - Owner and Employee access */}
        <Route path="/daily-khata-milk" element={
          <ProtectedRoute requiredSection="produce" requiredPermission="daily-khata-milk">
            <DailyMilkPage />
          </ProtectedRoute>
        } />
        
        <Route path="/animal-record" element={
          <ProtectedRoute requiredSection="produce" requiredPermission="animal-record">
            <WeeklyAnimalPage />
          </ProtectedRoute>
        } />
        
        {/* Accounting section routes - Owner only */}
        <Route path="/milk-purchase" element={
          <ProtectedRoute requiredSection="accounting" requiredPermission="milk-purchase">
            <MilkPurchase />
          </ProtectedRoute>
        } />

        <Route path="/goods-purchase" element={
          <ProtectedRoute requiredSection="accounting" requiredPermission="goods-purchase">
            <GoodsPurchase />
          </ProtectedRoute>
        } />

        <Route path="/sales" element={
          <ProtectedRoute requiredSection="accounting" requiredPermission="sales">
            <PurchaseHistoryPage />
          </ProtectedRoute>
        } />
        
        <Route path="/receipt" element={
          <ProtectedRoute requiredSection="accounting" requiredPermission="receipt">
            <PaymentHistoryPage />
          </ProtectedRoute>
        } />

        <Route path="/payment-history" element={
          <ProtectedRoute requiredSection="accounting" requiredPermission="payment-history">
            <SupplierPayment />
          </ProtectedRoute>
        } />

        <Route path="/animal-purchase" element={
          <ProtectedRoute requiredSection="accounting" requiredPermission="animal-purchase">
            <AnimalPurchasePage />
          </ProtectedRoute>
        } />
        
        {/* Billing section routes */}
        <Route path="/bill-generator" element={
          <ProtectedRoute requiredSection="billing" requiredPermission="bill-generator">
            <BillGeneratorPage />
          </ProtectedRoute>
        } />

        <Route path="/party-ledger" element={
          <ProtectedRoute requiredSection="billing" requiredPermission="party-ledger">
            <PartyLedgerPage />
          </ProtectedRoute>
        } />

        <Route path="/cashbook" element={
          <ProtectedRoute requiredSection="accounting" requiredPermission="cashbook">
            <CashbookPage />
          </ProtectedRoute>
        } />

        <Route path="/bank-statement" element={
          <ProtectedRoute requiredSection="accounting" requiredPermission="bank-statement">
            <BankStatementPage />
          </ProtectedRoute>
        } />

        <Route path="/owner-dashboard" element={
          <ProtectedRoute>
            <OwnerDashboardPage />
          </ProtectedRoute>
        } />
        
        {/* Settings - owner only */}
        <Route path="/settings" element={
          <ProtectedRoute requiredSection="portfolio" requiredPermission="settings">
            <SettingsPage />
          </ProtectedRoute>
        } />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </AuthProvider>
    </ToastProvider>
  );
}

export default App;